import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryResult,
  useQueryClient,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type { IAPI } from "./APIService";
import {
  type OrganisationAccessStatus,
  OrganisationAssociationType,
  type OrganisationRelationship,
} from "@/types/api/contracts/organisations";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type {
  BusinessType,
  LinkedAccountsOrderByParams,
  StripeStatus,
} from "@/components/screens/ManageAccounts/LinkedAccounts/Types/LinkedAccountsTypes";

export interface IRegisterVenueParams {
  email: string;
  name: string;
  password: string;
  telephone: string;
  business_type: BusinessType;

  address1?: string;
  town?: string;
  postcode?: string;
  contact_email?: string;
  contact_number?: string;
  account_name?: string;
  companies_house_number?: string;
  vat_number?: string;
  pin?: number | string;
  pin_protected_permissions?: Array<string>;
  country_code?: string;

  organisation?: {
    association_type: OrganisationAssociationType;
    organisation_identifier: string;
    start_date: string;
    end_date: string;
  };
}

export interface IOrganisationRelationshipResponse
  extends APIPaginatedResponse<OrganisationRelationship> {}

export interface IOrganisationRelationshipParams {
  search: string;
  access_status: OrganisationAccessStatus;
  payouts_enabled: boolean;
  stripe_status: StripeStatus;
  association_type: OrganisationAssociationType;
  page?: number;
  page_size?: number;
  order_by?: LinkedAccountsOrderByParams | string;
  valid?: boolean; // Whether the relationship is valid (first party _or_ third party and within the start and end dates)
  tags?: Array<ID>;
}

export type IOrganisationOneTimePasscode = {
  code: string;
  created_at: string;
  device_id: string | null;
  is_active: boolean;
  organisation_id: number | null;
  organisation_name: string | null;
  user_id: number;
  venue_id: number;
} & {
  is_loading: boolean;
};

export interface IOrganisationOneTimePasscodesParams {
  venue_id: number;
}

export interface IOrganisationRelationshipResponseAll {
  pages: IOrganisationRelationshipResponse[];
  pageParams: number[];
}

export interface IOrganisationThirdPartyLinkResponse
  extends APIPaginatedResponse<IOrganisationThirdPartyLink> {}

export interface IOrganisationThirdPartyLinkParams {
  page?: number;
  page_size?: number;
}

export interface IOrganisationThirdPartyLinkCreateParams {
  name: string;
  start_date: string;
  end_date: string;
}

export interface IOrganisationThirdPartyLink {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  identifier: string;
  association_type: OrganisationAssociationType;
}

export interface IOrganisationThirdPartyLinkUpdateParams
  extends IOrganisationThirdPartyLinkCreateParams {
  id: number;
}

export interface IOrganisationThirdPartyLinkDeleteParams {
  id: number;
}

export interface IOrganisationService {
  listAllRelationships: (
    params: IOrganisationRelationshipParams,
  ) => UseInfiniteQueryResult<IOrganisationRelationshipResponseAll, Error>;

  listRelationships: (
    params: IOrganisationRelationshipParams,
  ) => UseQueryResult<IOrganisationRelationshipResponse, Error>;

  // Register Venue
  registerVenue: () => UseMutationResult<
    {
      retrieval_token: string;
    },
    Error,
    IRegisterVenueParams
  >;
  dataSourceListQueryRelationships: (
    params: Partial<IOrganisationRelationshipParams>,
  ) => Promise<AxiosResponse<IOrganisationRelationshipResponse>>;

  // One Time Passcodes
  listOneTimePasscodes: (
    params: IOrganisationOneTimePasscodesParams,
  ) => UseQueryResult<Array<IOrganisationOneTimePasscode>, Error>;

  // One Time Passcodes
  createOneTimePasscode: (
    params: IOrganisationOneTimePasscodesParams,
  ) => UseMutationResult<
    IOrganisationOneTimePasscode,
    Error,
    IOrganisationOneTimePasscodesParams
  >;

  // Third Party Links
  listThirdPartyLinks: (
    params: IOrganisationThirdPartyLinkParams,
  ) => UseQueryResult<IOrganisationThirdPartyLinkResponse, Error>;
  createThirdPartyLink: (
    listParams: IOrganisationThirdPartyLinkParams,
  ) => UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkCreateParams
  >;
  updateThirdPartyLink: (
    listParams: IOrganisationThirdPartyLinkParams,
  ) => UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkUpdateParams
  >;
  deleteThirdPartyLink: (
    listParams: IOrganisationThirdPartyLinkParams,
  ) => UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkDeleteParams
  >;
}

export class OrganisationService implements IOrganisationService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  listAllRelationships(
    params: IOrganisationRelationshipParams,
  ): UseInfiniteQueryResult<IOrganisationRelationshipResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) =>
          (key === "tags" && value.length > 0) ||
          (key !== "tags" && value !== null),
      ),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "relationships", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IOrganisationRelationshipResponse>
        >("/organisations/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 200 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IOrganisationRelationshipResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  listRelationships(
    params: IOrganisationRelationshipParams,
  ): UseQueryResult<IOrganisationRelationshipResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) =>
          (key === "tags" && value.length > 0) ||
          (key !== "tags" && value !== null),
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "relationships"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IOrganisationRelationshipResponse>
        >("/organisations/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  async dataSourceListQueryRelationships(
    params: Partial<IOrganisationRelationshipParams>,
  ): Promise<AxiosResponse<IOrganisationRelationshipResponse>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );

    return this.api.get<AxiosResponse<IOrganisationRelationshipResponse>>(
      "/organisations/me/relationships/",
      {
        endpointService: EndpointService.Gateway,
        params: updatedParams,
      },
    );
  }

  listOneTimePasscodes(
    params: IOrganisationOneTimePasscodesParams,
  ): UseQueryResult<Array<IOrganisationOneTimePasscode>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "one-time-passcodes"],
        venueID: params.venue_id,
        params: params,
      }),
      queryFn: async () => {
        if (params.venue_id == null) {
          return [];
        }

        const { data } = await this.api.get<
          AxiosResponse<Array<IOrganisationOneTimePasscode>>
        >("/auth/login_code_logs", {
          endpointService: EndpointService.Gateway,
          venueSpecific: true,
          params: params,
        });

        return data;
      },
      enabled: params.venue_id !== null,
    });
  }

  createOneTimePasscode(
    params: IOrganisationOneTimePasscodesParams,
  ): UseMutationResult<
    IOrganisationOneTimePasscode,
    Error,
    IOrganisationOneTimePasscodesParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["organisation", "one-time-passcodes", "create"],
      mutationFn: async (mutationParams) => {
        const { data } = await this.api.post<
          AxiosResponse<IOrganisationOneTimePasscode>
        >("/auth/generate_code/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: true,
          params: params,
        });
        return data;
      },
      onMutate: async (newPasscode) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: ["organisation", "one-time-passcodes"],
        });

        // Snapshot the previous value
        const previousPasscodes = queryClient.getQueryData<
          Array<IOrganisationOneTimePasscode>
        >([
          "organisation",
          "one-time-passcodes",
          { venueId: newPasscode.venue_id },
          { venue_id: newPasscode.venue_id },
        ]);

        // Optimistically update to the new value
        queryClient.setQueryData<
          Array<IOrganisationOneTimePasscode> | undefined
        >(
          [
            "organisation",
            "one-time-passcodes",
            { venueId: newPasscode.venue_id },
            { venue_id: newPasscode.venue_id },
          ],
          (old) =>
            old
              ? [
                  {
                    ...newPasscode,
                    created_at: new Date().toISOString(),
                    is_active: true,
                    code: "Generating OTP...", // Add a placeholder for the code
                    device_id: "", // Add a placeholder for the device_id
                    organisation_id: 0, // Add a placeholder for the organisation_id
                    organisation_name: "", // Add a placeholder for the organisation_name
                    user_id: 0, // Add a placeholder for the user_id
                    is_loading: true,
                  } as IOrganisationOneTimePasscode,
                  ...old,
                ]
              : [],
        );

        // Return a context object with the snapshotted value
        return { previousPasscodes };
      },
      onError: (err, newPasscode, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData(
          [
            "organisation",
            "one-time-passcodes",
            { venueId: newPasscode.venue_id },
            { venue_id: newPasscode.venue_id },
          ],
          context?.previousPasscodes,
        );
      },
      onSettled: (data, error, variables) => {
        // Always refetch after error or success:
        queryClient.invalidateQueries({
          queryKey: [
            "organisation",
            "one-time-passcodes",
            { venueId: variables.venue_id },
            { venue_id: variables.venue_id },
          ],
        });
      },
    });
  }

  registerVenue(): UseMutationResult<
    {
      retrieval_token: string;
    },
    Error,
    IRegisterVenueParams
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "auth", "registerVenue"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params: IRegisterVenueParams) => {
        const updatedParams = Object.fromEntries(
          Object.entries(params).filter(
            ([key, value]) => value !== undefined || value !== null,
          ),
        );
        const { data } = await this.api.post<
          AxiosResponse<{
            retrieval_token: string;
          }>
        >("/restaurants/register/", {
          endpointService: EndpointService.Core,
          venueSpecific: false,
          params: updatedParams,
        });
        return data;
      },
      onMutate: () => {},
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["organisation", "relationships"],
        });
      },
      onError: () => {},
    });
  }

  listThirdPartyLinks(
    params: IOrganisationThirdPartyLinkParams,
  ): UseQueryResult<IOrganisationThirdPartyLinkResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "third-party-links"],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IOrganisationThirdPartyLinkResponse>
        >("/organisations/invites/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: params,
        });
        return data;
      },
    });
  }

  createThirdPartyLink(
    listParams: IOrganisationThirdPartyLinkParams,
  ): UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkCreateParams
  > {
    const queryClient = useQueryClient();

    const listQueryKey = buildQueryKey({
      queryKey: ["organisation", "third-party-links"],
      venueID: this.api.selectedVenue,
      params: listParams,
    });

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["organisation", "third-party-links", "create"],
        venueID: this.api.selectedVenue,
        params: null,
      }),
      mutationFn: async (mutationParams) => {
        const { data } = await this.api.post<
          AxiosResponse<IOrganisationThirdPartyLink>
        >("/organisations/invites/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...mutationParams,
            association_type: OrganisationAssociationType.third_party,
          },
        });
        return data;
      },
      onMutate: async (newThirdPartyLink) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: listQueryKey,
        });

        // Snapshot the previous value
        const previousThirdPartyLinks =
          queryClient.getQueryData<IOrganisationThirdPartyLinkResponse>(
            listQueryKey,
          );

        // Optimistically update to the new value
        queryClient.setQueryData<
          IOrganisationThirdPartyLinkResponse | undefined
        >(listQueryKey, (old) => {
          if (!old) return undefined;

          return {
            ...old,
            results: [
              {
                id: -1, // Temporary ID for optimistic update
                identifier: "pending", // Temporary identifier
                association_type: OrganisationAssociationType.third_party,
                ...newThirdPartyLink,
              } as IOrganisationThirdPartyLink,
              ...old.results,
            ],
            count: old.count + 1,
            next: old.next,
            previous: old.previous,
          };
        });

        // Return a context object with the snapshotted value
        return { previousThirdPartyLinks };
      },
      onError: (err, newThirdPartyLink, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData(
          listQueryKey,
          context?.previousThirdPartyLinks,
        );
      },
      onSettled: () => {
        // Always refetch after error or success:
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
        });
      },
    });
  }

  updateThirdPartyLink(
    listParams: IOrganisationThirdPartyLinkParams,
  ): UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkUpdateParams
  > {
    const queryClient = useQueryClient();

    const listQueryKey = buildQueryKey({
      queryKey: ["organisation", "third-party-links"],
      venueID: this.api.selectedVenue,
      params: listParams,
    });

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["organisation", "third-party-links", "update"],
        venueID: this.api.selectedVenue,
        params: null,
      }),
      mutationFn: async (mutationParams) => {
        const { id, ...rest } = mutationParams;

        const { data } = await this.api.patch<
          AxiosResponse<IOrganisationThirdPartyLink>
        >(`/organisations/invites/${id}/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...rest,
            association_type: OrganisationAssociationType.third_party,
          },
        });
        return data;
      },
      onMutate: async (updatedThirdPartyLink) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["organisation", "third-party-links"],
            venueID: this.api.selectedVenue,
            params: listParams,
          }),
        });

        // Snapshot the previous value
        const previousThirdPartyLinks =
          queryClient.getQueryData<IOrganisationThirdPartyLinkResponse>(
            listQueryKey,
          );

        // Optimistically update to the new value
        queryClient.setQueryData<
          IOrganisationThirdPartyLinkResponse | undefined
        >(listQueryKey, (old) => {
          if (!old) return undefined;
          return {
            ...old,
            results: old.results.map((result) =>
              result.id === updatedThirdPartyLink.id
                ? { ...result, ...updatedThirdPartyLink }
                : result,
            ),
            count: old.count,
            next: old.next,
            previous: old.previous,
          };
        });

        // Return a context object with the snapshotted value
        return { previousThirdPartyLinks };
      },
      onError: (err, updatedThirdPartyLink, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData(
          listQueryKey,
          context?.previousThirdPartyLinks,
        );
      },
      onSettled: () => {
        // Always refetch after error or success:
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
        });
      },
    });
  }

  deleteThirdPartyLink(
    listParams: IOrganisationThirdPartyLinkParams,
  ): UseMutationResult<
    IOrganisationThirdPartyLink,
    Error,
    IOrganisationThirdPartyLinkDeleteParams
  > {
    const queryClient = useQueryClient();

    const listQueryKey = buildQueryKey({
      queryKey: ["organisation", "third-party-links"],
      venueID: this.api.selectedVenue,
      params: listParams,
    });

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["organisation", "third-party-links", "delete"],
        venueID: this.api.selectedVenue,
        params: null,
      }),
      mutationFn: async (mutationParams) => {
        const { id, ...rest } = mutationParams;

        const { data } = await this.api.delete<
          AxiosResponse<IOrganisationThirdPartyLink>
        >(`/organisations/invites/${id}/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: rest,
        });
        return data;
      },
      onMutate: async (deletedThirdPartyLink) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: listQueryKey,
        });

        // Snapshot the previous value
        const previousThirdPartyLinks =
          queryClient.getQueryData<IOrganisationThirdPartyLinkResponse>(
            listQueryKey,
          );

        // Optimistically update to the new value
        queryClient.setQueryData<
          IOrganisationThirdPartyLinkResponse | undefined
        >(listQueryKey, (old) => {
          if (!old) return undefined;

          return {
            ...old,
            results: old.results.filter(
              (result) => result.id !== deletedThirdPartyLink.id,
            ),
            count: old.count - 1,
            next: old.next,
            previous: old.previous,
          };
        });

        // Return a context object with the snapshotted value
        return { previousThirdPartyLinks };
      },
      onError: (err, deletedThirdPartyLink, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData(
          listQueryKey,
          context?.previousThirdPartyLinks,
        );
      },
      onSettled: () => {
        // Always refetch after error or success:
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
        });
      },
    });
  }
}
